import { useQuery, useMutation } from '@tanstack/react-query';
import { bookingService } from '@leoclinic/services';
import type { BookingFormData } from '@leoclinic/types';

const BOOKING_KEY = ['booking'] as const;

function localDateString(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

export function usePublicTreatments(clinicId: string | null) {
  return useQuery({
    queryKey: [...BOOKING_KEY, 'treatments', clinicId],
    queryFn: () => bookingService.listPublicTreatments(clinicId!),
    enabled: !!clinicId,
  });
}

export function useAppointmentSettings(clinicId: string | null) {
  return useQuery({
    queryKey: [...BOOKING_KEY, 'appointment-settings', clinicId],
    queryFn: () => bookingService.getAppointmentSettings(clinicId!),
    enabled: !!clinicId,
  });
}

export function useAvailableSlots(
  clinicId: string | null,
  therapistId: string,
  treatmentId: string,
  maxAdvanceDays = 60,
) {
  const fromDate = localDateString(new Date());
  const to = new Date();
  to.setDate(to.getDate() + maxAdvanceDays);
  const toDate = localDateString(to);

  return useQuery({
    queryKey: [...BOOKING_KEY, 'slots', clinicId, therapistId, treatmentId, fromDate, toDate],
    queryFn: () =>
      bookingService.getAvailableSlots(clinicId!, therapistId, treatmentId, fromDate, toDate),
    enabled: !!clinicId && !!therapistId && !!treatmentId,
    staleTime: 30_000,
  });
}

export function usePublicTherapists(clinicId: string | null) {
  return useQuery({
    queryKey: [...BOOKING_KEY, 'therapists', clinicId],
    queryFn: () => bookingService.listPublicTherapists(clinicId!),
    enabled: !!clinicId,
  });
}

export function useOnlineBooking(clinicId: string | null) {
  return useMutation({
    mutationFn: (data: BookingFormData) => bookingService.createOnlineBooking(clinicId!, data),
  });
}

export function usePublicClinic(slug: string | null) {
  return useQuery({
    queryKey: [...BOOKING_KEY, 'public-clinic', slug],
    queryFn: () => bookingService.getPublicClinic(slug!),
    enabled: !!slug,
    staleTime: 5 * 60 * 1000,
  });
}

export { BOOKING_KEY };
